import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Shape returned by GET /api/StaticData/FeatureFlags.
 * Mirrors TaskManager.Domain.Configuration.FeatureFlagsOptions on the server.
 */
export interface IFeatureFlags {
  region: string;
}

export const DEPLOYMENT_REGIONS = {
  US: 'US',
  EU: 'EU',
} as const;

const DEFAULT_FLAGS: IFeatureFlags = {
  region: DEPLOYMENT_REGIONS.US,
};

/**
 * Loads and caches runtime feature flags from the TaskManager API.
 *
 * The flags are fetched once during application bootstrap (see the
 * APP_INITIALIZER wiring in `app.module.ts`) and synchronously readable
 * thereafter via `flags` / `isEu()`.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagsService {
  private _flags: IFeatureFlags = DEFAULT_FLAGS;
  private _loaded = false;

  constructor(private readonly http: HttpClient) {}

  /**
   * Fetches the flags from the server. Any failure (network error, 401, 404)
   * leaves the flags in their default state so the app degrades gracefully.
   */
  async load(): Promise<void> {
    if (this._loaded) return;
    try {
      const result = await firstValueFrom(
        this.http.get<IFeatureFlags>(
          `${environment.taskManagerServiceEndPoint}/StaticData/FeatureFlags`
        )
      );
      this._flags = { ...DEFAULT_FLAGS, ...(result ?? {}) };
    } catch {
      this._flags = DEFAULT_FLAGS;
    } finally {
      this._loaded = true;
    }
  }

  get flags(): IFeatureFlags {
    return this._flags;
  }

  get region(): string {
    return this._flags.region;
  }

  isEu(): boolean {
    return (this._flags.region ?? '').toUpperCase() === DEPLOYMENT_REGIONS.EU;
  }
}
